import type { ReactNode } from "react";
import type { BaseOrganismProps } from "@/components/types";

/**
 * Modal route parameters for URL handling
 */
export interface ModalRouteParams {
  type: string;
  id?: string;
  [key: string]: string | undefined;
}

/**
 * Background route state for preserving navigation context
 * Enhanced to support nested modal backgrounds
 */
export interface ModalBackgroundState {
  pathname: string;
  search?: string;
  hash?: string;
  state?: any;
  /**
   * Optional metadata for nested modal support
   */
  metadata?: {
    /**
     * Timestamp when this background state was created
     */
    timestamp?: number;
    /**
     * Modal ID that created this background state
     */
    modalId?: string;
    /**
     * Whether this background represents a modal route
     */
    isModalRoute?: boolean;
  };
}

/**
 * Modal configuration interface
 */
export interface ModalConfig {
  /**
   * Modal type for routing (e.g., 'product', 'order')
   */
  type: string;

  /**
   * Optional modal ID for specific item
   */
  id?: string;

  /**
   * Additional URL parameters
   */
  params?: Record<string, string>;

  /**
   * Background route to restore when modal closes
   */
  background?: ModalBackgroundState;

  /**
   * Modal behavior options
   */
  options?: ModalOptions;
}

/**
 * Modal behavior options
 */
export interface ModalOptions {
  /**
   * Whether modal can be closed by clicking overlay
   */
  closeOnOverlayClick?: boolean;

  /**
   * Whether modal can be closed by pressing escape key
   */
  closeOnEscape?: boolean;

  /**
   * Whether to use fullscreen on mobile devices
   */
  fullscreenOnMobile?: boolean;

  /**
   * Maximum width for the modal
   */
  maxWidth?:
    | "xs"
    | "sm"
    | "md"
    | "lg"
    | "xl"
    | "x2l"
    | "x3l"
    | "x4l"
    | "x5l"
    | "x6l"
    | "x7l"
    | false;

  /**
   * Whether to show close button in header
   */
  showCloseButton?: boolean;

  /**
   * Custom z-index for the modal
   */
  zIndex?: number;

  /**
   * Modal type for factory pattern customization
   */
  modalType?: string;

  /**
   * Drawer anchor position
   * @default "bottom"
   */
  anchor?: "top" | "left" | "bottom" | "right";
}

/**
 * Modal window props extending base organism props
 */
export interface ModalWindowProps extends BaseOrganismProps {
  /**
   * Whether the modal is open
   */
  open: boolean;

  /**
   * Function to call when modal should close
   */
  onClose: () => void;

  /**
   * Modal title (optional)
   */
  title?: string;

  /**
   * Modal content
   */
  children: ReactNode;

  /**
   * Modal behavior options
   */
  options?: ModalOptions;

  /**
   * Loading state
   */
  loading?: boolean;

  /**
   * Error state
   */
  error?: Error | null;
}

/**
 * Modal state for hook management
 */
export interface ModalState {
  /**
   * Whether modal is currently open
   */
  isOpen: boolean;

  /**
   * Current modal configuration
   */
  config: ModalConfig | null;

  /**
   * Loading state
   */
  loading: boolean;

  /**
   * Error state
   */
  error: Error | null;
}

/**
 * Modal actions for hook management
 */
export interface ModalActions {
  /**
   * Open modal with configuration
   */
  openModal: (config: ModalConfig) => void;

  /**
   * Close modal and optionally navigate to background
   */
  closeModal: (navigateToBackground?: boolean) => void;

  /**
   * Set loading state
   */
  setLoading: (loading: boolean) => void;

  /**
   * Set error state
   */
  setError: (error: Error | null) => void;

  /**
   * Clear modal state
   */
  clearModal: () => void;
}

/**
 * Modal router actions for navigation and state management
 */
export interface ModalRouterActions {
  /**
   * Navigate to modal with type and optional ID
   */
  navigateToModal: (
    type: string,
    id?: string,
    params?: Record<string, string>
  ) => void;

  /**
   * Navigate back to background route
   */
  navigateBack: () => void;

  /**
   * Get current modal route params
   */
  getModalParams: () => ModalRouteParams | null;

  /**
   * Check if currently in modal route
   */
  isModalRoute: () => boolean;

  /**
   * Get background route state
   */
  getBackgroundState: () => ModalBackgroundState | null;

  // State management methods
  /**
   * Set loading state for current modal
   */
  setLoading: (modalId: string, loading: boolean) => void;

  /**
   * Clear loading state for current modal
   */
  clearLoading: (modalId: string) => void;

  /**
   * Set error state for current modal
   */
  setError: (modalId: string, error: Error | null) => void;

  /**
   * Clear error state for current modal
   */
  clearError: (modalId: string) => void;

  /**
   * Get current modal loading state
   */
  isLoading: (modalId?: string) => boolean;

  /**
   * Get current modal error state
   */
  getError: (modalId?: string) => Error | null;

  /**
   * Clear all state for current modal
   */
  clearModalState: (modalId: string) => void;

  /**
   * Initialize modal state
   */
  initializeModalState: (modalId: string) => void;
}

/**
 * Combined modal hook return type
 */
export interface UseModalReturn extends ModalState, ModalActions {
  /**
   * Router-specific actions
   */
  router: ModalRouterActions;
}

/**
 * Default modal options
 */
export const DEFAULT_MODAL_OPTIONS: Required<ModalOptions> = {
  closeOnOverlayClick: true,
  closeOnEscape: true,
  fullscreenOnMobile: true,
  maxWidth: "x2l",
  showCloseButton: true,
  zIndex: 1300, // Material-UI Modal default z-index
  modalType: "default",
  anchor: "bottom",
};

/**
 * Modal types enum for route matching
 */
export enum ModalType {
  PRODUCT = "product",
  ORDER = "order",
  CATEGORY = "category",
  USER = "user",
}

/**
 * Modal Route Interface for Declarative Modal Routing
 *
 * Allows defining modal routes like react-router:
 * - path: URL pattern with parameters (e.g., "/product/:id")
 * - component: React component to render
 * - modalOptions: configuration for ModalWindow
 * - getProps: function to generate props from URL parameters
 */
export interface ModalRoute {
  /** URL pattern with parameters (e.g., "/product/:id", "/order") */
  path: string;

  /** React component to render in modal */
  component: React.ComponentType<any>;

  /** Optional modal window configuration */
  modalOptions?: Partial<ModalOptions>;

  /**
   * Optional function to generate component props from URL parameters
   * @param params - URL parameters extracted from path
   * @param context - Modal context with navigation methods
   * @returns Props object for the component
   */
  getProps?: (
    params: Record<string, string>,
    context: ModalRouteContext
  ) => object;
}

/**
 * Modal Route Context
 *
 * Provides navigation methods and utilities to modal components
 */
export interface ModalRouteContext {
  /** Close current modal */
  onClose: () => void;

  /** Navigate to another modal */
  navigateToModal: (type: string, id?: string) => void;

  /** Navigate back */
  navigateBack: () => void;

  /** Current modal parameters */
  params: Record<string, string>;

  /** Current modal type */
  modalType: string;
}

/**
 * Modal Route Match Result
 *
 * Result of matching a URL path against modal routes
 */
export interface ModalRouteMatch {
  /** Matched modal route */
  route: ModalRoute;

  /** Extracted URL parameters */
  params: Record<string, string>;

  /** Modal type extracted from path */
  modalType: string;
}
